import { makeAutoObservable } from "mobx";
import MeasurementModel from "../../api/models/MeasurementModel";
import ExportMeasurementXLSXService from "../../api/services/ExportMeasurementXLSXService";
import MeasurementRowListService from "../../api/services/MeasurementRowListService";

import { ERowTypes } from "api/enums";
import ContractModel from "api/models/ContractModel";
import ContractRowModel from "api/models/ContractRowModel";
import MeasurementRowModel from "api/models/MeasurementRowModel";
import WBSModel from "api/models/WBSModel";
import CalculateMeasurementRowService from "api/services/CalculateMeasurementRowService";
import ExportMeasurementPDFService from "api/services/ExportMeasurementPDFService";
import {
  IExportMeasurementExtraData,
  IExportMeasurementRowData,
  IExportMeasurementValue,
} from "components/interfaces";
import jsPDF from "jspdf";
import {
  CompanyStore,
  ContractRowStore,
  ContractStore,
  ExportStore,
  MeasurementListStore,
  MeasurementRowsStore,
  MeasurementStore,
} from "..";
import { sum, valueToPercentual } from "../../utils/DecimalPrecision";

class Store {
  constructor() {
    makeAutoObservable(this);
  }

  /** Inicia a exportação dos relatórios de medições
   * do contrato aberto.
   */
  public async export(
    type: string,
    selectedStagesCodes: string[]
  ): Promise<boolean> {
    const measurement = MeasurementStore.currentMeasurement;
    const contract = ContractStore.getCurrentContract();
    if (!measurement || !contract) return false;

    const company = CompanyStore.currentCompany;
    const accumulatedColumnData = MeasurementRowsStore.measuredTotalizer;
    const filterMeasured = type.includes("onlyMeasured");
    const { rows, currentToBillingTotal } =
      this.getRowsAndCalculateBillingTotal(filterMeasured, selectedStagesCodes);

    const extraData = {
      currentToBillingTotal,
      accumulatedColumnData,
      companyImage: await ExportStore.convertCompanyImageToBase64(
        company?.image
      ),
      companyName: company?.name,
    };

    switch (type) {
      case "xlsx-current":
        return await this.exportCurrentMeasurementXLSX(
          contract,
          measurement,
          rows,
          extraData
        );
      case "xlsx-onlyMeasured":
        return await this.exportCurrentMeasurementXLSX(
          contract,
          measurement,
          rows,
          extraData
        );
      case "xlsx-all":
        return await this.exportAllMeasurementsXLSX(
          contract,
          measurement,
          rows,
          extraData
        );
      case "pdf-current":
        return await this.exportCurrentMeasurementPDF(
          contract,
          measurement,
          rows,
          extraData
        );
      case "pdf-onlyMeasured":
        return await this.exportCurrentMeasurementPDF(
          contract,
          measurement,
          rows,
          extraData
        );
      case "pdf-all":
        return await this.exportAllMeasurementsPDF(
          contract,
          measurement,
          rows,
          extraData
        );
      default:
        return false;
    }
  }

  /** Exporta o relatório PDF com todas as medições
   * do contrato aberto.
   */
  private async exportAllMeasurementsXLSX(
    contract: ContractModel,
    measurement: MeasurementModel,
    rows: IExportMeasurementRowData[],
    extra: IExportMeasurementExtraData
  ) {
    await MeasurementListStore.getAllMeasurementsData();
    const allMeasurements = this.getAllMeasurements();

    const workbook = await ExportMeasurementXLSXService.createAllMeasurements(
      contract,
      measurement,
      rows,
      allMeasurements,
      extra
    );

    await workbook.saveAs(contract.name + "_" + measurement.description);
    return true;
  }

  /** Exporta o relatório Excel da medição atualmente
   * aberta do contrato.
   */
  private async exportCurrentMeasurementXLSX(
    contract: ContractModel,
    measurement: MeasurementModel,
    rows: IExportMeasurementRowData[],
    extra: IExportMeasurementExtraData
  ) {
    const workbook =
      await ExportMeasurementXLSXService.createWorksheetCurrentMeasurement(
        contract,
        measurement,
        rows,
        extra
      );

    await workbook.saveAs(contract.name + "_" + measurement.description);
    return true;
  }

  /** Exporta o relatório PDF com todas as medições
   * do contrato aberto.
   */
  private async exportAllMeasurementsPDF(
    contract: ContractModel,
    measurement: MeasurementModel,
    rows: IExportMeasurementRowData[],
    extra: IExportMeasurementExtraData
  ) {
    await MeasurementListStore.getAllMeasurementsData();
    const allMeasurements = this.getAllMeasurements();

    const pdfDoc = ExportMeasurementPDFService.createAllMeasurements(
      contract,
      measurement,
      rows,
      extra,
      allMeasurements
    );

    this.savePDFFile(pdfDoc, contract.name + "_" + measurement.description);
    return true;
  }

  /** Exporta o relatório PDF da medição atualmente
   * aberta do contrato.
   */
  private async exportCurrentMeasurementPDF(
    contract: ContractModel,
    measurement: MeasurementModel,
    rows: IExportMeasurementRowData[],
    extra: IExportMeasurementExtraData
  ) {
    const pdfDoc = ExportMeasurementPDFService.createPageCurrentMeasurement(
      contract,
      measurement,
      rows,
      extra
    );

    this.savePDFFile(pdfDoc, contract.name + "_" + measurement.description);
    return true;
  }

  /** Salva o arquivo PDF na máquina local do usuário. */
  private async savePDFFile(doc: jsPDF, name: string) {
    const fileName = name.replace(/\s/g, "");
    doc.save(`${fileName}.pdf`);
  }

  /** Obtém os dados de todas as linhas da medição atualmente aberta
   * que serão exportados no relatório.
   */
  public getRowsAndCalculateBillingTotal(
    filterIsFilled: boolean,
    selectedStagesCodes: string[]
  ): {
    rows: IExportMeasurementRowData[];
    currentToBillingTotal: number;
  } {
    const allRows = ContractRowStore.getAllRows();
    if (!allRows) return { rows: [], currentToBillingTotal: 0 };

    const listRows = MeasurementRowsStore.getAllRows();
    const rowsFormatted: IExportMeasurementRowData[] = [];
    let currentToBillingTotal = 0;

    allRows.forEach((contractRow) => {
      const measurementRow =
        listRows && contractRow
          ? MeasurementRowListService.getRowFromList(listRows, contractRow)
          : undefined;

      const measureData = this.prepareMeasurementDataOfRow(
        contractRow,
        measurementRow
      );
      const contractTotal = ContractStore.currentContractTotal;

      if (
        contractRow.rowType === ERowTypes.RESOURCE &&
        typeof measureData.currentToBilling.total === "number"
      ) {
        currentToBillingTotal = sum(
          currentToBillingTotal,
          measureData.currentToBilling.total
        );
      }

      if (filterIsFilled && (!measurementRow || !measurementRow.isFilled)) {
        return;
      }

      if (
        !this.checkIfBelongsToSelectedStage(
          contractRow.structured,
          selectedStagesCodes
        )
      ) {
        return;
      }

      rowsFormatted.push({
        ...measureData,
        uid: contractRow.uid,
        structured: contractRow.structured,
        description: contractRow.description,
        rowType: contractRow.rowType,
        quantity: contractRow.quantity,
        unit: contractRow.unit,
        unitCost: contractRow.unitCost,
        total: contractRow.total,
        percentual: contractRow.getPercentual(contractTotal),
      });
    });

    return { rows: rowsFormatted, currentToBillingTotal };
  }

  private checkIfBelongsToSelectedStage(
    structured: string,
    selectedStagesCodes: string[]
  ): boolean {
    if (!selectedStagesCodes.length) return true;
    return selectedStagesCodes.some(
      (code) => code === structured || structured.startsWith(`${code}.`)
    );
  }

  /** Define os dados de medição atual, medição acumulada e saldo a medir
   * da linha.
   */
  private prepareMeasurementDataOfRow(
    contractRow: ContractRowModel,
    measurementRow?: MeasurementRowModel
  ): {
    currentMeasurement: IExportMeasurementValue;
    measured: IExportMeasurementValue;
    currentToBilling: IExportMeasurementValue;
  } {
    const defaultValues = { total: "-", quantity: "-", percentual: "-" };
    const hasMeasured = contractRow.hasMeasuredValues();

    // Se a linha não tem valor acumulado então retorna um objeto padrao
    if (!hasMeasured) {
      return {
        currentMeasurement: { ...defaultValues },
        measured: { ...defaultValues },
        currentToBilling: {
          total: contractRow.total,
          quantity: contractRow.quantity,
          percentual: 100,
        },
      };
    }

    // Se tiver uma linha medição retorna os valores da linha medida
    if (measurementRow) {
      let data: any = {
        measured: MeasurementRowsStore.getMeasuredData(measurementRow),
        currentToBilling: { ...measurementRow.currentToBilling },
        currentMeasurement: {
          ...defaultValues,
        },
      };

      if (measurementRow.isFilled) {
        const currentData = measurementRow.currentMeasurement;
        data.currentMeasurement = {
          total: currentData.total,
          quantity: currentData.quantity,
          percentual: valueToPercentual(currentData.total, contractRow.total),
        };
      }

      return data;
    }

    // Se não tiver medição mas tem valores acumulados, devolve os valores acumulados e saldo a medir
    const blankRow = MeasurementRowModel.create(contractRow.uid, {
      structured: contractRow.structured,
    });
    blankRow.attachContractRow(contractRow);
    CalculateMeasurementRowService.calculateMeasurementValues(
      blankRow,
      blankRow.currentMeasurement
    );

    return {
      measured: MeasurementRowsStore.getMeasuredData(blankRow),
      currentToBilling: { ...blankRow.currentToBilling },
      currentMeasurement: { ...defaultValues },
    };
  }

  /** Obtém todas as linhas de cada medição do contrato. */
  private getAllMeasurements(): {
    data: MeasurementModel;
    wbs: WBSModel<MeasurementRowModel>;
  }[] {
    const allData = MeasurementListStore.getMeasurements().reduce(
      (list, measurement) => {
        const wbs = MeasurementListStore.getRowsListByUid(measurement.uid);
        if (wbs) list.push({ data: measurement, wbs });

        return list;
      },
      [] as any[]
    );

    return allData;
  }
}

const ExportMeasurementStore = new Store();
export default ExportMeasurementStore;
